import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ExportDateRangeEnum } from 'API';
import { RootState } from 'app/store/rootReducer';
import { format, isValid } from 'date-fns';
import { transactionService } from 'app/services/transaction.service';

/**
 * SEE-1235: only the fields the export sheets read. The enqueue response and a CDK live update
 * both fit; the Amplify API.ts types don't, as they need __typename, enums and fields the CDK
 * payload doesn't carry.
 */
export interface DownloadQueueRow {
  id: string;
  s3_key?: string | null;
  cron_status?: string | null;
  mark_as_exported?: boolean | null;
  exported_count?: number | null;
  skipped_count?: number | null;
}

export interface ReceiptsDownloadQueueRow {
  id: string;
  s3_key?: string | null;
  cron_status?: string | null;
  error_code?: string | null;
}

export interface DownloadTransactionsState {
  download?: DownloadQueueRow;
  receiptsDownload?: ReceiptsDownloadQueueRow;
}

export const initialState: DownloadTransactionsState = {
  download: undefined,
  receiptsDownload: undefined,
};

export interface PrepareDownloadArgs {
  paidolId: string;
  columns: string[];
  startDate?: Date;
  endDate?: Date;
  rangeOption: ExportDateRangeEnum;
  onlyMyTransactions?: boolean;
  markAsExported?: boolean;
}

export interface PrepareReceiptsDownloadArgs {
  paidolId: string;
  rangeOption?: ExportDateRangeEnum;
  startDate?: Date | string;
  endDate?: Date | string;
  filteredTransactionIds?: string[];
  onlyMyTransactions?: boolean;
}

// This function is for spreadsheet data download. SEE-857 D5: routes through
// the v2-transaction Lambda's enqueueTransactionsDownload mutation, which
// asserts paidol membership before writing the queue row. user_id and
// paidolUserId are derived server-side from the JWT and PaidolUser GSI.
export const prepareDownload = createAsyncThunk(
  'overview/transactions/prepareDownload',
  async (
    {
      paidolId,
      columns,
      startDate,
      endDate,
      rangeOption,
      onlyMyTransactions,
      markAsExported,
    }: PrepareDownloadArgs,
    { dispatch }
  ) => {
    let formattedStartDate: string | undefined;
    let formattedEndDate: string | undefined;

    if (startDate) {
      if (isValid(startDate)) {
        formattedStartDate = format(startDate, 'yyyy-MM-dd');
      }
    }

    if (endDate) {
      if (isValid(endDate)) {
        formattedEndDate = format(endDate, 'yyyy-MM-dd');
      }
    }

    const result = await transactionService.enqueueTransactionsDownload({
      paidolId,
      columns,
      rangeOption,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      onlyMyTransactions,
      markAsExported,
    });

    dispatch(
      setDownload({
        id: result.id,
        cron_status: result.cron_status,
        mark_as_exported: markAsExported,
      })
    );
  }
);

// SEE-857 D5: routes through the v2-transaction Lambda's
// enqueueReceiptsDownload mutation. Same pattern as prepareDownload —
// caller-from-JWT, paidol-membership-asserted, server-set ids.
export const prepareReceiptsDownload = createAsyncThunk(
  'overview/transactions/prepareReceiptsDownload',
  async (
    {
      paidolId,
      rangeOption,
      startDate,
      endDate,
      filteredTransactionIds = [],
      onlyMyTransactions,
    }: PrepareReceiptsDownloadArgs,
    { dispatch }
  ) => {
    let formattedStartDate: string | undefined;
    let formattedEndDate: string | undefined;

    if (startDate) {
      const startDateObject = new Date(startDate);
      if (isValid(startDateObject)) {
        formattedStartDate = format(startDateObject, 'yyyy-MM-dd');
      }
    }

    if (endDate) {
      const endDateObject = new Date(endDate);
      if (isValid(endDateObject)) {
        formattedEndDate = format(endDateObject, 'yyyy-MM-dd');
      }
    }

    const result = await transactionService.enqueueReceiptsDownload({
      paidolId,
      rangeOption: rangeOption || ExportDateRangeEnum.TODAY,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      filteredTransactionIds,
      onlyMyTransactions,
    });

    dispatch(
      setReceiptsDownload({
        id: result.id,
        cron_status: result.cron_status,
      })
    );
  }
);

const downloadTransactionsSlice = createSlice({
  name: 'overview/transactions/downloadTransactions',
  initialState,
  reducers: {
    // SEE-1574: return the initial state. Reassigning the `state` parameter only rebinds a local
    // under Immer, so the old version left a pending export (and its s3_key) behind.
    resetDownloadTransactions: () => initialState,
    setDownload: (state, action: PayloadAction<DownloadQueueRow | undefined>) => {
      state.download = action.payload;
    },
    setReceiptsDownload: (state, action: PayloadAction<ReceiptsDownloadQueueRow | undefined>) => {
      state.receiptsDownload = action.payload;
    },
  },
});

export const selectDownloadTransactionsSlice = (state: RootState) =>
  state.cards?.downloadTransactions ?? initialState;

export const { resetDownloadTransactions, setDownload, setReceiptsDownload } =
  downloadTransactionsSlice.actions;

export default downloadTransactionsSlice.reducer;
